import { Tooltip, Typography, TypographyProps } from '@mui/material';
import * as React from 'react';
import type { JSX } from 'react';

import { colorSystem } from '../../../theme';
import { Flexbox } from '../../Flexbox';
import { InfoFilledIcon } from '../../icons/filledIcons';
import { Text } from '../../Text';
import { StandardLayoutProps } from '../../types';

export interface FormItemProps extends StandardLayoutProps {
    label?: string | JSX.Element;
    LabelProps?: TypographyProps;
    description?: string | JSX.Element;
    variant?: 'description-as-tooltip' | 'description-inlined';
    required?: boolean;
    actions?: JSX.Element;
}

interface FormItemLabel {
    labelId: string;
    /** The one field the label names; with several, the FormItem is a group the label names instead. */
    labelledFieldId: string | undefined;
    register: (fieldId: string, field: HTMLElement) => (() => void) | undefined;
}

const FormItemLabelContext = React.createContext<FormItemLabel | null>(null);

/**
 * The id of the enclosing FormItem's label, for the field's `aria-labelledby`, when the field is the only one in the
 * FormItem without a name of its own. `enabled` is false for a field that has one.
 */
export function useFormItemLabelId(fieldRef: React.RefObject<HTMLElement>, enabled: boolean): string | undefined {
    const fieldId = React.useId();
    const context = React.useContext(FormItemLabelContext);
    const register = context?.register;

    React.useEffect(() => {
        if (!enabled || !register || !fieldRef.current) {
            return;
        }
        return register(fieldId, fieldRef.current);
    }, [enabled, register, fieldId, fieldRef]);

    return enabled && context?.labelledFieldId === fieldId ? context.labelId : undefined;
}

export function FormItem({
    label,
    children,
    description,
    LabelProps = {},
    required,
    variant = 'description-as-tooltip',
    actions,
    ...rest
}: React.PropsWithChildren<FormItemProps>): JSX.Element {
    const generatedLabelId = React.useId();
    const labelId = LabelProps.id ?? generatedLabelId;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [fieldIds, setFieldIds] = React.useState<string[]>([]);

    const register = React.useCallback((fieldId: string, field: HTMLElement) => {
        // Popovers and dialogs render their fields in a portal, outside the FormItem; the label isn't about them.
        if (!containerRef.current?.contains(field)) {
            return undefined;
        }
        setFieldIds((ids) => [...ids, fieldId]);
        return () => setFieldIds((ids) => ids.filter((id) => id !== fieldId));
    }, []);

    // One field takes the label as its name. Several, like a date range or a table of inputs, keep their own
    // names, and the label names the group around them, so they don't all read the same.
    const hasLabel = Boolean(label);
    const isGroup = hasLabel && fieldIds.length > 1;
    const labelledFieldId = hasLabel && fieldIds.length === 1 ? fieldIds[0] : undefined;
    const labelContext = React.useMemo(
        () => ({ labelId, labelledFieldId, register }),
        [labelId, labelledFieldId, register],
    );

    return (
        <Flexbox
            ref={containerRef}
            gap={8}
            role={isGroup ? 'group' : undefined}
            aria-labelledby={isGroup ? labelId : undefined}
            {...rest}
            sx={[
                {
                    flexDirection: 'column',
                },
                ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
            ]}
        >
            {label && variant === 'description-as-tooltip' && (
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Flexbox
                        gap={4}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {description && variant === 'description-as-tooltip' && (
                            <Tooltip title={description} arrow>
                                <InfoFilledIcon size={16} color={colorSystem.neutral[5]} aria-hidden />
                            </Tooltip>
                        )}
                        {/* TODO: use Text here hopefully */}
                        <Typography
                            variant="h4"
                            id={labelId}
                            {...LabelProps}
                            sx={[
                                {
                                    display: 'inline',
                                    color: colorSystem.neutral[8],
                                },
                                ...(Array.isArray(LabelProps.sx) ? LabelProps.sx : [LabelProps.sx]),
                            ]}
                        >
                            {label}
                        </Typography>
                        {required && <span style={{ fontSize: '18px', color: colorSystem.neutral[7] }}>*</span>}
                    </Flexbox>
                    {actions && <span style={{ marginLeft: 'auto' }}>{actions}</span>}
                </Flexbox>
            )}
            {label && variant === 'description-inlined' && (
                <Flexbox
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    <Flexbox
                        gap={4}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {/* TODO: use Text here hopefully */}
                        <Typography
                            variant="h4"
                            id={labelId}
                            {...LabelProps}
                            sx={[
                                {
                                    display: 'inline',
                                    color: colorSystem.neutral[8],
                                },
                                ...(Array.isArray(LabelProps.sx) ? LabelProps.sx : [LabelProps.sx]),
                            ]}
                        >
                            {label}
                        </Typography>
                        {required && <span style={{ fontSize: '18px', color: colorSystem.neutral[7] }}>*</span>}
                        {actions && <span style={{ marginLeft: 'auto' }}>{actions}</span>}
                    </Flexbox>
                    {description && variant === 'description-inlined' && (
                        <Text
                            variant={'body-small'}
                            style={{
                                color: colorSystem.neutral[7],
                                textWrap: 'pretty',
                            }}
                        >
                            {description}
                        </Text>
                    )}
                </Flexbox>
            )}
            <FormItemLabelContext.Provider value={labelContext}>{children}</FormItemLabelContext.Provider>
        </Flexbox>
    );
}
